import { useEffect, useMemo, useState } from "react";
import { contactsListName, instructionsFilesLibName, ListFieldsType, siteUrl } from "src/api/config";
import { siteUrlOrSiteApi } from "src/api/config/systemSwitch";
import { Modal } from "src/components/DialogComponent";
import HelpIcon from "src/components/ui-icons/menu/HelpIcon";
import { DownloadIcon, FileIcon } from "src/components/ui-icons/upload";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { getItems } from "src/modules/spItems/actions";
import { selectItems } from "src/modules/spItems/selectors";
import { FileType } from "src/types/main";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { useFilePreview, isPreviewable } from "src/hooks/useFilePreview";
import EyeIcon from "src/components/ui-icons/reports/EyeIcon";
import Loading from "src/components/Loading";
import { isPWA } from "src/utils/mobile";
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
const siteUrlProd = 'https://supply.napitkivmeste.ru'; // для корректного отображения превью

export default function HelpPageMobile() {
  const dispatch = useAppDispatch();
  const contacts = useAppSelector((state) =>
    selectItems(state, contactsListName),
  ) as ListFieldsType[typeof contactsListName][];
  const instructions = useAppSelector((state) => selectItems(state, instructionsFilesLibName)) as FileType[];

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [viewerTitle, setViewerTitle] = useState<string>("");
  const [viewerObjectUrl, setViewerObjectUrl] = useState<string | null>(null);
  const [viewerBlob, setViewerBlob] = useState<Blob | null>(null);
  const [viewerSourceUrl, setViewerSourceUrl] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const { openPreview, previewLoading, previewFileId, PreviewModal } = useFilePreview(siteUrl, siteUrlProd);
  const isPreviewableDoc = (file: FileType) =>
    isPreviewable(file.FileRef) || /\.(pdf)$/.test((file.FileLeafRef || "").toLowerCase());

  const toRelativePath = (ref: string) => {
    try {
      return new URL(ref).pathname.replace(/\/{2,}/g, "/");
    } catch {
      return ref;
    }
  };

  const handleEyeClick = (file: FileType) => {
    if (/\.pdf$/.test((file.FileLeafRef || "").toLowerCase())) {
      openViewer(file); // pdf — существующий вьювер страницы (iframe)
    } else {
      openPreview(toRelativePath(file.FileRef), file.ID);
    }
  };

  const revokeViewerUrl = () => {
    if (viewerObjectUrl) {
      try {
        URL.revokeObjectURL(viewerObjectUrl);
      } catch {
        // ignore
      }
    }
    setViewerObjectUrl(null);
    setViewerBlob(null);
    setViewerSourceUrl(null);
  };

  useEffect(() => {
    dispatch(getItems({ listName: contactsListName }));
    dispatch(getItems({ listName: instructionsFilesLibName }));
  }, [dispatch]);

  const isIOS = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    // iPadOS can present as Mac; include touch heuristic.
    const iOSDevice = /iPad|iPhone|iPod/.test(ua);
    const iPadOS = ua.includes("Mac") && "ontouchend" in document;
    return iOSDevice || iPadOS;
  }, []);

  const forceDownloadUrl = useMemo(() => {
    return (url: string) => {
      if (!url) return url;
      // SharePoint: adding `download=1` often forces attachment behavior.
      if (url.includes("download=1")) return url;
      return url.includes("?") ? `${url}&download=1` : `${url}?download=1`;
    };
  }, []);

  const buildFetchUrl = useMemo(() => {
    return (ref: string) => {
      if (!ref) return "";

      const trimmed = ref.trim();
      // If already absolute, just use it.
      if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
      return siteUrlOrSiteApi + encodeURI(trimmed);
    };
  }, []);

  const buildInstructionUrl = useMemo(() => {
    return (ref: string) => {
      const url = buildFetchUrl(ref);
      return url ? forceDownloadUrl(url) : "#";
    };
  }, [buildFetchUrl, forceDownloadUrl]);

  const openViewer = async (file: FileType) => {
    setViewerError(null);
    setShareError(null);
    setViewerTitle(file.FileLeafRef || "Инструкция");
    setViewerOpen(true);
    setViewerLoading(true);

    revokeViewerUrl();

    try {
      const url = buildFetchUrl(file.FileRef || "");
      if (!url) throw new Error("Нет ссылки на файл");
      setViewerSourceUrl(url);

      const name = (file.FileLeafRef || "").toLowerCase();
      const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
      const previewable =
        ext === ".pdf" ||
        ext === ".png" ||
        ext === ".jpg" ||
        ext === ".jpeg" ||
        ext === ".gif" ||
        ext === ".webp" ||
        ext === ".svg";

      // For non-previewable types (xlsx/docx/etc) do not fetch:
      // we can't render them inline anyway; user should open in browser.
      if (!previewable) {
        setViewerLoading(false);
        return;
      }

      let token: string | null = null;

      if (!isPWA()) {
        const result = await SecureStoragePlugin.get({ key: 'accessToken' });
        token = result.value;
      }
      const headers = {
        ...(token && { Authorization: `Bearer ${token}` })
      };

      const response = await fetch(url, {
        credentials: "include",
        headers
      });

      if (!response.ok) throw new Error(`Ошибка загрузки файла (${response.status})`);

      const blob = await response.blob();
      setViewerBlob(blob);

      const objectUrl = URL.createObjectURL(blob);
      setViewerObjectUrl(objectUrl);
    } catch (err: any) {
      setViewerError(err?.message || "Не удалось открыть инструкцию");
    } finally {
      setViewerLoading(false);
    }
  };

  const handleInstructionClick = async (e: any, file: FileType) => {
    // In browsers we keep the default behavior (download/open via href).
    // In iOS WebView we intercept and open viewer to avoid "blank screen" after downloads.
    if (!isIOS) return;
    e.preventDefault();
    e.stopPropagation();
    await openViewer(file);
  };

  const shareInstruction = async () => {
    setShareError(null);

    if (!viewerBlob) {
      setShareError("Файл ещё не загружен");
      return;
    }

    // Web Share API is the most reliable way to "download" from iOS in-app webviews:
    // it opens the native share sheet where user can choose "Save to Files".
    const nav: any = navigator as any;
    if (!nav?.share) {
      setShareError("Сохранение недоступно в этой версии приложения. Откройте инструкцию в браузере.");
      return;
    }

    try {
      const file = new File([viewerBlob], viewerTitle || "instruction", {
        type: viewerBlob.type || "application/octet-stream",
        lastModified: Date.now(),
      });

      if (nav?.canShare && !nav.canShare({ files: [file] })) {
        setShareError("Сохранение недоступно в этой версии приложения. Откройте инструкцию в браузере.");
        return;
      }

      await nav.share({
        title: viewerTitle || "Инструкция",
        files: [file],
      });
    } catch (err: any) {
      // User cancel throws AbortError in many browsers — treat as non-error.
      if (err?.name === "AbortError") return;
      setShareError("Не удалось открыть меню «Поделиться». Попробуйте ещё раз.");
    }
  };

  return (
    <div>
      <div className="px-6 sm:px-7.5 py-[16px] tablet:py-[26px] tablet:border-b tablet:border-[#DFE4EA]-500 mmm">
        <div className="flex items-center gap-2 hover:cursor-pointer w-fit">

          <span>
            <HelpIcon />
          </span>
          <span>Помощь</span>
        </div>
      </div>
      <div className="tablet:px-6 pb-6 pt-0 tablet-pt-6 sm:p-7.5">
        <div className="flex flex-col gap-5 mb-5 sm:grid sm:grid-cols-2">
          {contacts.map(el => (
            <div key={el.ID} className="p-4 tablet:p-5 sm:p-7.5 border rounded-lg bg-white shadow-custom w-full gap-[10px] tablet:gap-3 sm:gap-5 flex flex-col">

              <h4 className="text-black text-[13px] tablet:text-[18px] font-medium leading-6">{el.Title}</h4>
              <div className="text-[12px] tablet:text-[16px]">
                <span>Телефон: </span>
                <a className="text-darkblue" href={`tel:${el.Phone}`}>
                  {el.Phone}
                </a>
              </div>

              <div className="text-[12px] tablet:text-[16px]">
                <span>Адрес: </span>
                <a className="text-darkblue" href={`mailto:${el.Email}`}>
                  {el.Email}
                </a>
              </div>
            </div>
          ))}
        </div>
        <div className="p-5 sm:p-7.5 border rounded-lg bg-white shadow-custom w-full">
          <h4 className="text-black text-[13px] tablet:text-[18px] font-medium leading-6 mb-[10px] tablet:mb-5">Инструкции</h4>
          <FlexCol className="gap-3 sm:gap-5">
            <div className="flex flex-row">
              <div className="flex flex-col gap-y-3 sm:gap-y-5 w-full">
                {instructions.map(file => (
                  <a
                    href={buildInstructionUrl(file.FileRef || "")}
                    onClick={(e) => handleInstructionClick(e, file)}
                    download={file.FileLeafRef}
                    rel="noreferrer"
                    key={file.ID}
                    className="flex flex-row justify-between gap-3 px-4 py-3 rounded-lg cursor-pointer upload bg-sp-gray"
                  >
                    <div className="flex flex-col grow w-[60%]">
                      <div className="flex flex-row gap-x-2.5">
                        <div className="flex flex-col justify-center items-center">
                          <FileIcon />
                        </div>
                        <div className="flex flex-col text-[14px] hyphens-auto tablet:text-[16px]">{file.FileLeafRef.toLocaleLowerCase()}</div>
                      </div>
                    </div>

                    <FlexRow className="gap-3 items-center">
                      {isPreviewableDoc(file) && (
                        <div
                          className="flex flex-col justify-center items-center"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEyeClick(file);
                          }}
                        >
                          {previewLoading && previewFileId === file.ID ? (
                            <FlexRow className="w-6 h-6">
                              <Loading />
                            </FlexRow>
                          ) : (
                            <EyeIcon />
                          )}
                        </div>
                      )}
                      <div className="flex flex-col justify-center items-center download-icon">
                        <DownloadIcon />
                      </div>
                    </FlexRow>
                  </a>
                ))}
              </div>
            </div>
          </FlexCol>
        </div>
      </div>

      <Modal
        open={viewerOpen}
        toggleDialog={(b) => {
          if (!b) {
            setViewerOpen(false);
            setViewerLoading(false);
            setViewerError(null);
            setShareError(null);
            revokeViewerUrl();
          } else {
            setViewerOpen(true);
          }
        }}
        zIndex={120}
        closeOnBackdrop
        modalClassName="p-0 w-full sm:min-w-[600px] max-w-[calc(100vw-5%)] max-h-[calc(100dvh-80px)] overflow-hidden"
      >
        <header className="px-6 pt-6 pb-4 border-b flex flex-col gap-3">
          <h2 className="text-base font-medium text-black break-words">{viewerTitle}</h2>
          <div className="flex items-center justify-end gap-4">
            <button
              type="button"
              className="text-sm text-[#012169] underline disabled:opacity-40"
              onClick={shareInstruction}
              disabled={viewerLoading || !!viewerError || !viewerBlob}
            >
              Сохранить
            </button>
            {viewerSourceUrl && (
              <a
                className="text-sm text-[#012169] underline"
                href={forceDownloadUrl(viewerSourceUrl)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Открыть в браузере
              </a>
            )}
          </div>
        </header>

        <div className="px-6 py-4 overflow-auto">
          {viewerLoading && <div className="text-[#637381]">Загрузка...</div>}
          {viewerError && <div className="text-[#BA0C2F] mb-3">{viewerError}</div>}
          {shareError && <div className="text-[#BA0C2F] mb-3">{shareError}</div>}

          {!viewerLoading && !viewerError && (
            <>
              {(() => {
                const name = (viewerTitle || "").toLowerCase();
                const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
                const isPdf = ext === ".pdf";
                const isImage =
                  ext === ".png" || ext === ".jpg" || ext === ".jpeg" || ext === ".gif" || ext === ".webp" || ext === ".svg";

                const src = viewerObjectUrl || viewerSourceUrl || null;
                if (!src) return null;

                if (isImage) return <img src={src} alt={viewerTitle} className="w-full h-auto select-none" />;
                if (isPdf) return <iframe title={viewerTitle} src={src} className="w-full" style={{ height: "70vh", border: "none" }} />;

                return (
                  <div className="text-[#637381]">
                    Этот тип файла нельзя отобразить внутри приложения. Нажмите «Открыть в браузере».
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </Modal>

      {PreviewModal}

    </div>
  );
}
